/**
 * What every preview pass receives for one frame (W2-01a preview-gl; rendering.md R§1.1, R§10).
 *
 * Spaces and their matrices to clip space (the QuadBatch transform):
 *  - list px: device pixels the FrameDrawList was computed for (`viewport.winW` x `winH`, y down). When the canvas
 *    has another size (render scale changed between the engine frame and the draw) everything is scaled by
 *    (kx, ky) = target size / list size, so a frame is never drawn misplaced;
 *  - world (chart space, y up): px = (vx + (x + 1) W/2, vy + H/2 - y W/2);
 *  - UI (camera space, y down): px = (vx + (x + 1) W/2, vy + H/2 + y W/2).
 */

import type { FrameDrawList, Viewport } from '../../core/engine/types';
import type { Mat2D, RGBAf } from '../../core/util/types';
import { clipFromPx, mulMat, type GlState } from '../gl/glState';
import type { LinkedProgram } from '../gl/program';
import { colorByte, type QuadBatch } from '../gl/quadBatch';
import type { TextureCache } from '../gl/textures';
import type { LoadedSkin, TextureProvider } from '../types';
import type { PreviewFonts } from './fonts';
import type { TextCache } from './textCache';

export interface PassPrograms {
  /** Textured quad x vertex colour, straight alpha. */
  readonly quad: LinkedProgram;
  /** Filled ellipse, premultiplied output (paint stamps). */
  readonly disc: LinkedProgram;
  /** Plain texture copy. */
  readonly copy: LinkedProgram;
}

export interface PassContext {
  readonly gl: WebGL2RenderingContext;
  readonly state: GlState;
  readonly batch: QuadBatch;
  readonly programs: PassPrograms;
  readonly textures: TextureCache;
  readonly text: TextCache;
  readonly fonts: PreviewFonts;
  readonly list: FrameDrawList;
  readonly vp: Viewport;
  /** Framebuffer of the frame being drawn (null = the canvas); passes that draw elsewhere rebind it. */
  targetFbo: WebGLFramebuffer | null;
  /** Size of the bound target in px. */
  readonly targetW: number;
  readonly targetH: number;
  /** Target px per list px. */
  readonly kx: number;
  readonly ky: number;
  readonly pxToClip: Mat2D;
  readonly worldToClip: Mat2D;
  readonly uiToClip: Mat2D;
  /** Device px per world (and UI) unit, horizontally / vertically (W/2 scaled by kx / ky). */
  readonly pxPerUnitX: number;
  readonly pxPerUnitY: number;
  readonly provider: TextureProvider | null;
  readonly skin: LoadedSkin | null;
}

/** Chart space -> list px. */
export function worldToPx(vp: Viewport): Mat2D {
  return [vp.W / 2, 0, 0, -vp.W / 2, vp.vx + vp.W / 2, vp.vy + vp.H / 2];
}

/** UI space -> list px. */
export function uiToPx(vp: Viewport): Mat2D {
  return [vp.W / 2, 0, 0, vp.W / 2, vp.vx + vp.W / 2, vp.vy + vp.H / 2];
}

/** The three space matrices of a target. */
export function spaceMatrices(
  vp: Viewport,
  targetW: number,
  targetH: number,
): { kx: number; ky: number; pxToClip: Mat2D; worldToClip: Mat2D; uiToClip: Mat2D } {
  const kx = vp.winW > 0 ? targetW / vp.winW : 1;
  const ky = vp.winH > 0 ? targetH / vp.winH : 1;
  const pxToClip = mulMat(clipFromPx(targetW, targetH), [kx, 0, 0, ky, 0, 0]);
  return {
    kx,
    ky,
    pxToClip,
    worldToClip: mulMat(pxToClip, worldToPx(vp)),
    uiToClip: mulMat(pxToClip, uiToPx(vp)),
  };
}

/** Colour bytes of an RGBAf with an extra alpha factor (each channel clamped explicitly). */
export function rgbaBytes(c: RGBAf, alphaMul = 1): readonly [number, number, number, number] {
  return [colorByte(c[0]), colorByte(c[1]), colorByte(c[2]), colorByte(c[3] * alphaMul)];
}

/** The viewport of the list in target px (for scissors, effect quads and paint buffers). */
export function viewportPx(ctx: Pick<PassContext, 'vp' | 'kx' | 'ky'>): { x: number; y: number; w: number; h: number } {
  const { vp, kx, ky } = ctx;
  return { x: vp.vx * kx, y: vp.vy * ky, w: vp.W * kx, h: vp.H * ky };
}

/** Integer viewport rectangle in target px (clamped to the target). */
export function viewportPxInt(ctx: Pick<PassContext, 'vp' | 'kx' | 'ky' | 'targetW' | 'targetH'>): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const r = viewportPx(ctx);
  const x0 = Math.max(0, Math.min(ctx.targetW, Math.round(r.x)));
  const y0 = Math.max(0, Math.min(ctx.targetH, Math.round(r.y)));
  const x1 = Math.max(x0, Math.min(ctx.targetW, Math.round(r.x + r.w)));
  const y1 = Math.max(y0, Math.min(ctx.targetH, Math.round(r.y + r.h)));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Starts a batch of straight-alpha quads in a space. */
export function beginQuads(ctx: PassContext, xf: Mat2D, tex: WebGLTexture | null): void {
  const b = ctx.batch;
  b.setProgram(ctx.programs.quad);
  b.setBlend('alpha');
  b.setTransform(xf);
  b.setTexture(tex ?? ctx.textures.white);
}
